/**
 * Account > Profile picture: choose a photo, then move and zoom it inside a
 * circle to pick what shows. The crop is made here and sent as a small square
 * JPEG (see server/src/avatars.ts), so the original photo never leaves the browser.
 */
import { Trash2, Upload, ZoomIn, ZoomOut } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties, type DragEvent, type KeyboardEvent, type PointerEvent } from "react";
import { Avatar, Modal, Spinner } from "../components/ui";
import { api, errorMessage, type User } from "../lib/api";
import { toast, useSession } from "../lib/app";
import "./profile.css";

/** The square the photo moves around in, and the circle inside it, in CSS px. */
const STAGE = 300;
const MARGIN = 26;
const CIRCLE = STAGE - 2 * MARGIN;
/** Pixels across the uploaded picture: sharp at the largest size it's shown, on a high-DPI screen. */
const OUTPUT = 320;
/** Don't zoom in so far that fewer of the photo's pixels than this fill the circle. */
const MIN_SOURCE = 48;
const MAX_ZOOM = 10;

interface Photo {
  img: HTMLImageElement;
  url: string;
  width: number;
  height: number;
}

/** How far it's zoomed (1 = the photo's shorter side just fills the circle), and where its centre is from the circle's. */
interface View {
  zoom: number;
  x: number;
  y: number;
}

const START: View = { zoom: 1, x: 0, y: 0 };

/** CSS px per photo pixel at zoom 1. */
const coverScale = (p: Photo) => CIRCLE / Math.min(p.width, p.height);
const maxZoom = (p: Photo) => Math.max(1, Math.min(MAX_ZOOM, Math.min(p.width, p.height) / MIN_SOURCE));

/** Keep the zoom in range, and the photo covering the whole circle. */
function clamp(p: Photo, v: View): View {
  const zoom = Math.min(Math.max(v.zoom, 1), maxZoom(p));
  const s = coverScale(p) * zoom;
  const mx = Math.max(0, (p.width * s - CIRCLE) / 2);
  const my = Math.max(0, (p.height * s - CIRCLE) / 2);
  return { zoom, x: Math.min(Math.max(v.x, -mx), mx), y: Math.min(Math.max(v.y, -my), my) };
}

/** Zoom to `zoom`, keeping the bit of photo at (px, py) from the circle's centre where it is. */
function zoomAt(p: Photo, v: View, zoom: number, px = 0, py = 0): View {
  const z = Math.min(Math.max(zoom, 1), maxZoom(p));
  const k = z / v.zoom;
  return clamp(p, { zoom: z, x: px - (px - v.x) * k, y: py - (py - v.y) * k });
}

/** Where the photo sits, relative to the circle's top-left corner, drawn `size` px across instead of CIRCLE. */
function placement(p: Photo, v: View, size: number): CSSProperties {
  const s = coverScale(p) * v.zoom * (size / CIRCLE);
  const w = p.width * s;
  const h = p.height * s;
  const f = size / CIRCLE;
  return { left: size / 2 + v.x * f - w / 2, top: size / 2 + v.y * f - h / 2, width: w, height: h };
}

/** The part of the photo inside the circle, as an OUTPUT px square JPEG. */
async function render(p: Photo, v: View): Promise<Blob> {
  const s = coverScale(p) * v.zoom;
  let side = CIRCLE / s;
  let sx = Math.min(Math.max(p.width / 2 - (v.x + CIRCLE / 2) / s, 0), p.width - side);
  let sy = Math.min(Math.max(p.height / 2 - (v.y + CIRCLE / 2) / s, 0), p.height - side);
  let source: CanvasImageSource = p.img;
  // Halve a big photo in steps: shrinking it in one go skips pixels and looks grainy.
  while (side > OUTPUT * 2) {
    const half = Math.round(side / 2);
    const step = document.createElement("canvas");
    step.width = step.height = half;
    const ctx = step.getContext("2d")!;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(source, sx, sy, side, side, 0, 0, half, half);
    [source, sx, sy, side] = [step, 0, 0, half];
  }
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = OUTPUT;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingQuality = "high";
  // JPEG has no transparency, so see-through parts of a PNG come out white rather than black.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, OUTPUT, OUTPUT);
  ctx.drawImage(source, sx, sy, side, side, 0, 0, OUTPUT, OUTPUT);
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't make the picture."))), "image/jpeg", 0.9));
}

async function openPhoto(file: File): Promise<Photo> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  try {
    // Not img.decode(): Chrome holds that until the page next paints, which a background tab may never do.
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
  } catch {
    URL.revokeObjectURL(url);
    throw new Error("That file isn't a picture this browser can open. Try a JPEG or PNG.");
  }
  return { img, url, width: img.naturalWidth, height: img.naturalHeight };
}

const hasFiles = (e: DragEvent) => e.dataTransfer.types.includes("Files");

/** Whose picture is being chosen: who the previews show, and how it's saved. */
export interface PictureFor {
  name: string;
  username: string;
  /** The dialog's title. */
  title: string;
  save: (image: Blob) => Promise<void>;
}

/**
 * Choosing someone's picture: a hidden file input and the crop dialog. Render
 * `element`, then call pick() from a click (browsers only open the file chooser
 * then), or open() with a dropped file.
 */
export function usePictureChooser() {
  const input = useRef<HTMLInputElement>(null);
  const [who, setWho] = useState<PictureFor | null>(null);
  const [photo, setPhoto] = useState<Photo | null>(null);
  useEffect(() => {
    if (photo) return () => URL.revokeObjectURL(photo.url);
  }, [photo]);

  /** Without `whose`, it's for the same person as last time ("Choose another photo"). */
  function pick(whose?: PictureFor) {
    if (whose) setWho(whose);
    input.current?.click();
  }

  async function open(file: File | undefined, whose?: PictureFor) {
    if (!file) return;
    try {
      const p = await openPhoto(file);
      if (whose) setWho(whose);
      setPhoto(p);
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }

  const element = (
    <>
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          open(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      {photo && who && <CropDialog photo={photo} who={who} onChooseAnother={() => pick()} onClose={() => setPhoto(null)} />}
    </>
  );
  return { pick, open, element };
}

export function ProfilePicture() {
  const { user, setUser } = useSession();
  const chooser = usePictureChooser();
  const [dropping, setDropping] = useState(false);
  const [removing, setRemoving] = useState(false);
  if (!user) return null;

  const me: PictureFor = {
    name: user.displayName,
    username: user.username,
    title: "Profile picture",
    save: async (image) => {
      setUser((await api.upload<{ user: User }>("/api/me/avatar", image)).user);
      toast("Profile picture saved", "success");
    },
  };
  const pick = () => chooser.pick(me);

  async function remove() {
    setRemoving(true);
    try {
      const r = await api.del<{ user: User }>("/api/me/avatar");
      setUser(r.user);
      toast("Profile picture removed", "success");
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    setRemoving(false);
  }

  return (
    <div
      className={`profile-picture${dropping ? " dropping" : ""}`}
      onDragOver={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setDropping(true);
      }}
      onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && setDropping(false)}
      onDrop={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setDropping(false);
        chooser.open(e.dataTransfer.files[0], me);
      }}
    >
      <button className="profile-picture-face" onClick={pick} aria-label={user.avatar ? "Change profile picture" : "Add a profile picture"}>
        <Avatar id={user.id} name={user.displayName} size={88} title={user.avatar ? "Change your picture" : "Add a picture"} />
      </button>
      <div className="stack" style={{ gap: 8 }}>
        <span className="field-label">Profile picture</span>
        <div className="row" style={{ flexWrap: "wrap" }}>
          <button className="btn" onClick={pick}>
            <Upload size={14} /> {user.avatar ? "Change picture" : "Upload a picture"}
          </button>
          {user.avatar && (
            <button className="btn btn-ghost" onClick={remove} disabled={removing}>
              <Trash2 size={14} /> Remove
            </button>
          )}
        </div>
        <span className="field-hint">Shown beside your name on cursors, comments and history. You can drop a photo here too.</span>
      </div>
      {chooser.element}
    </div>
  );
}

function CropDialog({ photo, who, onChooseAnother, onClose }: { photo: Photo; who: PictureFor; onChooseAnother: () => void; onClose: () => void }) {
  const [view, setView] = useState(START);
  const [saving, setSaving] = useState(false);
  const stage = useRef<HTMLDivElement>(null);
  /** Fingers (or the mouse) down on the photo, and where they were last. */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const v = clamp(photo, view);
  const most = maxZoom(photo);

  // A different photo starts centred and zoomed out.
  useEffect(() => setView(START), [photo]);

  /** A point on the page, from the circle's centre. */
  const fromCentre = (x: number, y: number) => {
    const r = stage.current!.getBoundingClientRect();
    return [x - r.left - STAGE / 2, y - r.top - STAGE / 2] as const;
  };

  // Scroll or pinch a trackpad to zoom where the pointer is. React's wheel listener is passive, so it can't stop the page scrolling.
  useEffect(() => {
    const el = stage.current!;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1);
      // Pinching sends small deltas with ctrlKey set.
      const factor = Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.002));
      const r = el.getBoundingClientRect();
      setView((prev) => zoomAt(photo, prev, prev.zoom * factor, e.clientX - r.left - STAGE / 2, e.clientY - r.top - STAGE / 2));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [photo]);

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    const ps = pointers.current;
    if (!ps.has(e.pointerId)) return;
    const [a0, b0] = [...ps.values()];
    ps.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const [a1, b1] = [...ps.values()];
    if (!b0 || !b1) {
      // One finger or the mouse: drag the photo.
      setView((prev) => clamp(photo, { ...prev, x: prev.x + a1.x - a0.x, y: prev.y + a1.y - a0.y }));
      return;
    }
    // Two fingers: pinch to zoom around them, and move with them.
    const spread = Math.hypot(b1.x - a1.x, b1.y - a1.y) / Math.max(1, Math.hypot(b0.x - a0.x, b0.y - a0.y));
    const [mx, my] = fromCentre((a1.x + b1.x) / 2, (a1.y + b1.y) / 2);
    const dx = (a1.x + b1.x - a0.x - b0.x) / 2;
    const dy = (a1.y + b1.y - a0.y - b0.y) / 2;
    setView((prev) => {
      const moved = clamp(photo, { ...prev, x: prev.x + dx, y: prev.y + dy });
      return zoomAt(photo, moved, moved.zoom * spread, mx, my);
    });
  }

  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => pointers.current.delete(e.pointerId);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const step = e.shiftKey ? 40 : 8;
    const move: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (move[e.key]) setView((prev) => clamp(photo, { ...prev, x: prev.x + move[e.key][0], y: prev.y + move[e.key][1] }));
    else if (e.key === "+" || e.key === "=") setView((prev) => zoomAt(photo, prev, prev.zoom * 1.15));
    else if (e.key === "-" || e.key === "_") setView((prev) => zoomAt(photo, prev, prev.zoom / 1.15));
    else if (e.key === "0") setView(START);
    else return;
    e.preventDefault();
  }

  async function save() {
    setSaving(true);
    try {
      await who.save(await render(photo, v));
      onClose();
    } catch (e) {
      toast(errorMessage(e), "error");
      setSaving(false);
    }
  }

  const preview = (size: number) => (
    <span className="crop-preview" style={{ width: size, height: size }}>
      <img src={photo.url} alt="" draggable={false} style={placement(photo, v, size)} />
    </span>
  );

  return (
    <Modal
      title={who.title}
      onClose={onClose}
      width={420}
      footer={
        <>
          <button className="btn btn-ghost crop-another" onClick={onChooseAnother} disabled={saving}>
            Choose another photo
          </button>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving && <Spinner />} Save picture
          </button>
        </>
      }
    >
      <p className="muted small crop-help">Drag to move it. Scroll, pinch or use the slider to zoom.</p>
      <div
        ref={stage}
        className="crop-stage"
        style={{ width: STAGE, height: STAGE }}
        tabIndex={0}
        aria-label="Your photo. Arrow keys move it, plus and minus zoom, 0 starts again."
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
      >
        <span className="crop-photo" style={{ inset: MARGIN }}>
          <img src={photo.url} alt="" draggable={false} style={placement(photo, v, CIRCLE)} />
        </span>
        <span className="crop-mask" style={{ inset: MARGIN }} />
      </div>
      <div className="crop-zoom">
        <button className="icon-btn" title="Zoom out" aria-label="Zoom out" disabled={v.zoom <= 1} onClick={() => setView(zoomAt(photo, v, v.zoom / 1.25))}>
          <ZoomOut size={16} />
        </button>
        <input
          type="range"
          aria-label="Zoom"
          min={0}
          max={Math.log(most)}
          step={0.001}
          value={Math.log(v.zoom)}
          disabled={most <= 1}
          onChange={(e) => setView(zoomAt(photo, v, Math.exp(Number(e.target.value))))}
        />
        <button className="icon-btn" title="Zoom in" aria-label="Zoom in" disabled={v.zoom >= most} onClick={() => setView(zoomAt(photo, v, v.zoom * 1.25))}>
          <ZoomIn size={16} />
        </button>
      </div>
      <div className="crop-previews" aria-hidden="true">
        {preview(88)}
        <span className="crop-preview-person">
          {preview(32)}
          <span>
            <span className="crop-preview-name">{who.name}</span>
            <span className="muted small">{who.username}</span>
          </span>
        </span>
        <span className="crop-preview-chip">
          {preview(20)}
          {who.name}
        </span>
      </div>
    </Modal>
  );
}
